import { useState, useEffect, useRef, useCallback, lazy, Suspense, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  Bell,
  Settings,
  Search,
  Filter,
  FilterX,
  Clock,
  Users,
  LogOut,
  User as UserIcon,
  FileText,
  Menu,
  X,
  LayoutDashboard as DashboardIcon,
  HelpCircle,
  Eye,
  Gauge,
  GitBranch,
  Network,
  ChevronLeft,
  BarChart3,
  HeartPulse,
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { ThemeToggle } from './ThemeToggle'
import { LanguageSelector } from './LanguageSelector'
import { NotificationBell } from './NotificationBell'
import { useTranslation } from 'react-i18next'
import { alertsApi } from '../api/alerts'
import { anomaliesApi } from '../api/anomalies'
import { healthChecksApi } from '../api/healthChecks'
import { useHelp } from '../context/HelpContext'
import { useWorkspaceHeader } from '../context/WorkspaceHeaderContext'
import { useTopologyHeader } from '../context/TopologyHeaderContext'
import { WorkspacePicker } from './workspaces/WorkspacePicker'
import { TopologyLayoutPicker } from './topology/TopologyLayoutPicker'
import { CommandPalette } from './CommandPalette'

import { VigiaIcon } from './ai/VigiaIcon'

const AiChatPanel = lazy(() => import('./ai/AiChatPanel'))

// Route preload map - triggers dynamic import on hover/focus for instant navigation
const preloadedRoutes = new Set<string>()
const routePreloads: Record<string, () => void> = {
  '/events': () => { import('../pages/Events') },
  '/live-tail': () => { import('../pages/LiveTail') },
  '/signals': () => { import('../pages/Signals') },
  '/dashboards': () => { import('../pages/Dashboards') },
  '/alerts': () => { import('../pages/Alerts') },
  '/anomalies': () => { import('../pages/Anomalies') },
  '/settings': () => { import('../pages/Settings') },
  '/users': () => { import('../pages/Users') },
  '/audit': () => { import('../pages/AuditLogs') },
  '/ingestion-rules': () => { import('../pages/IngestionRules') },
  '/retention-policies': () => { import('../pages/RetentionPolicies') },
  '/metrics': () => { import('../pages/Metrics') },
  '/traces': () => { import('../pages/Traces') },
  '/services': () => { import('../pages/Services') },
  '/usage': () => { import('../pages/UsageAnalytics') },
  '/health-checks': () => { import('../pages/HealthChecks') },
}

function preloadRoute(path: string) {
  if (preloadedRoutes.has(path)) return
  preloadedRoutes.add(path)
  routePreloads[path]?.()
}

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true')
  const { openContextualHelp } = useHelp()
  const isViewer = user?.role === 'Viewer'

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev
      localStorage.setItem('sidebar-collapsed', String(next))
      return next
    })
  }

  // Query alerts to show triggered count in sidebar
  const { data: alerts } = useQuery({
    queryKey: ['alerts'],
    queryFn: alertsApi.getAlerts,
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  const triggeredAlertsCount = alerts?.filter(a => a.state === 'Triggered').length ?? 0

  // Query anomalies to show active count in sidebar
  const { data: anomalyCount } = useQuery({
    queryKey: ['anomaly-active-count'],
    queryFn: anomaliesApi.getActiveCount,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const activeAnomaliesCount = anomalyCount?.count ?? 0

  // Query health check summary to show down count in sidebar
  const { data: healthCheckSummary } = useQuery({
    queryKey: ['health-checks', 'summary'],
    queryFn: healthChecksApi.getSummary,
    refetchInterval: 15000,
  })
  const healthChecksDownCount = healthCheckSummary?.down ?? 0

  // Keyboard shortcuts: ? for help, Shift+F for focus mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

      if (!isInput && (e.key === '?' || (e.key === '/' && e.shiftKey))) {
        e.preventDefault()
        openContextualHelp()
      }

      // Shift+F: toggle focus mode (collapse sidebar + filters)
      if (!isInput && e.shiftKey && e.key === 'F') {
        e.preventDefault()
        setCollapsed(prev => {
          const next = !prev
          localStorage.setItem('sidebar-collapsed', String(next))
          window.dispatchEvent(new CustomEvent('pcl-focus-mode', { detail: { collapsed: next } }))
          return next
        })
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [openContextualHelp])

  // Listen for focus-mode-toggle from CommandPalette action
  useEffect(() => {
    const handler = () => {
      setCollapsed(prev => {
        const next = !prev
        localStorage.setItem('sidebar-collapsed', String(next))
        window.dispatchEvent(new CustomEvent('pcl-focus-mode', { detail: { collapsed: next } }))
        return next
      })
    }
    window.addEventListener('pcl-focus-mode-toggle', handler)
    return () => window.removeEventListener('pcl-focus-mode-toggle', handler)
  }, [])

  const handleLogout = async () => {
    await logout()
  }

  const closeSidebar = () => setSidebarOpen(false)

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
      >
        {t('common.skipToContent', 'Skip to content')}
      </a>

      {/* Top Navbar */}
      <HeaderBar
        onOpenSidebar={() => setSidebarOpen(true)}
        onOpenHelp={openContextualHelp}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Sidebar */}
        <aside
          data-tour="sidebar"
          className={`
            fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-black text-gray-900 dark:text-white flex flex-col border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ease-in-out
            lg:static lg:translate-x-0 lg:relative
            ${collapsed ? 'lg:w-[68px]' : 'lg:w-64'}
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Collapse toggle button - positioned on the right edge of sidebar */}
          <button
            onClick={toggleCollapsed}
            className="hidden lg:flex absolute top-[14px] -right-3.5 z-50 w-[27px] h-[27px] items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 shadow-sm"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={`${collapsed ? 'Expand sidebar' : 'Collapse sidebar'} (Shift+F)`}
          >
            <ChevronLeft size={16} className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
          </button>
          {/* Mobile sidebar header with close button */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800 lg:hidden">
            <div className="flex items-center gap-2">
              <img src="/logo-vertical-75px.png" alt="Vigía" className="block dark:hidden h-7 w-auto" />
              <img src="/logo-vertical-200px-dark.png" alt="Vigía" className="hidden dark:block h-7 w-auto" />
              <span className="font-bold text-gray-900 dark:text-white">{t('sidebar.title')}</span>
            </div>
            <button
              onClick={closeSidebar}
              className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>

          <nav className={`flex-1 overflow-y-auto transition-all duration-300 ${collapsed ? 'lg:px-2 lg:py-3' : 'p-3'} p-3`}>
            <ul className="space-y-1">
              <NavItem to="/events" icon={<Search size={20} />} label={t('common.events')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-events" />
              <NavItem to="/traces" icon={<GitBranch size={20} />} label={t('nav.traces')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-traces" />
              <NavItem to="/services" icon={<Network size={20} />} label={t('nav.services')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-services" />
              <NavItem to="/live-tail" icon={<Activity size={20} />} label={t('nav.liveTail')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-livetail" />
              <NavItem to="/signals" icon={<Filter size={20} />} label={t('common.signals')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-signals" />
              <NavItem to="/dashboards" icon={<DashboardIcon size={20} />} label={t('common.dashboards')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-dashboards" />
              <NavItem to="/alerts" icon={<Bell size={20} />} label={t('common.alerts')} badge={triggeredAlertsCount} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-alerts" />
              <NavItem to="/anomalies" icon={<AlertTriangle size={20} />} label={t('common.anomalies')} badge={activeAnomaliesCount} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-anomalies" />
              <NavItem to="/health-checks" icon={<HeartPulse size={20} />} label={t('healthChecks.sidebar')} badge={healthChecksDownCount} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-healthchecks" />
            </ul>

            {/* Admin section */}
            {user?.role === 'Admin' && (
              <div className="mt-4 pt-3 border-t border-gray-50 dark:border-gray-700">
                <p className={`text-[10px] uppercase text-gray-400 dark:text-gray-500 font-bold tracking-widest mb-2 px-3 transition-all duration-300 ${collapsed ? 'lg:hidden' : ''}`}>
                  {t('common.administration')}
                </p>
                <ul className="space-y-1">
                  <NavItem to="/users" icon={<Users size={20} />} label={t('common.users')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-users" />
                  <NavItem to="/audit" icon={<FileText size={20} />} label={t('common.auditLogs')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-audit" />
                  <NavItem to="/ingestion-rules" icon={<FilterX size={20} />} label={t('ingestionRules.sidebar')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-ingestion" />
                  <NavItem to="/retention-policies" icon={<Clock size={20} />} label={t('retentionPolicies.sidebar')} collapsed={collapsed} onClick={closeSidebar} />
                  <NavItem to="/metrics" icon={<Gauge size={20} />} label={t('metrics.sidebar')} collapsed={collapsed} onClick={closeSidebar} />
                  <NavItem to="/usage" icon={<BarChart3 size={20} />} label={t('usage.sidebar')} collapsed={collapsed} onClick={closeSidebar} />
                </ul>
              </div>
            )}

            {/* Delegated permissions for non-admin users */}
            {user && user.role !== 'Admin' && (user.canManageRetention || user.canManageMetrics) && (
              <div className="mt-4 pt-3 border-t border-gray-50 dark:border-gray-700">
                <ul className="space-y-1">
                  {user.canManageRetention && (
                    <NavItem to="/retention-policies" icon={<Clock size={20} />} label={t('retentionPolicies.sidebar')} collapsed={collapsed} onClick={closeSidebar} />
                  )}
                  {user.canManageMetrics && (
                    <NavItem to="/metrics" icon={<Gauge size={20} />} label={t('metrics.sidebar')} collapsed={collapsed} onClick={closeSidebar} />
                  )}
                </ul>
              </div>
            )}
          </nav>

          <div className={`border-t border-gray-50 dark:border-gray-700 ${collapsed ? 'lg:px-2 lg:py-2' : 'p-3'} p-3 transition-all duration-300`}>
            <ul className="space-y-1">
              <NavItem to="/settings" icon={<Settings size={20} />} label={t('common.settings')} collapsed={collapsed} onClick={closeSidebar} dataTour="nav-settings" />
            </ul>
          </div>

          {/* User info */}
          {user && (
            <div className={`border-t border-gray-50 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 ${collapsed ? 'lg:px-2 lg:py-2' : 'px-3 py-2'} px-3 py-2 transition-all duration-300`}>
              <div className={`flex items-center ${collapsed ? 'lg:justify-center' : 'gap-3'}`}>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0" title={collapsed ? user.displayName : undefined}>
                  <UserIcon size={16} className="text-white" />
                </div>
                <div className={`flex-1 min-w-0 transition-all duration-300 ${collapsed ? 'lg:hidden' : ''}`}>
                  <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{user.displayName}</p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate uppercase tracking-tighter">{t(`users.modal.roles.${user.role}`)}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className={`p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${collapsed ? 'lg:hidden' : ''}`}
                  aria-label={t('common.logout')}
                >
                  <LogOut size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* Proconsi logo */}
          <div className={`border-t border-gray-50 dark:border-gray-700 flex justify-center ${collapsed ? 'lg:hidden' : 'p-3'} p-3 transition-all duration-300`}>
            <ShimmerLogo />
          </div>
        </aside>

        {/* Main content */}
        <main id="main-content" className="flex-1 overflow-auto bg-gray-50/30 dark:bg-gray-900/10 flex flex-col">
          {/* Read-only banner for Viewer role */}
          {isViewer && (
            <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 flex items-center justify-center gap-2">
              <Eye size={16} className="text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                {t('common.readOnlyMode')}
              </span>
            </div>
          )}
          <div className="flex-1 overflow-auto">
            {children}
          </div>
        </main>
      </div>

      <CommandPalette />
      <Suspense fallback={null}>
        <AiChatPanel />
      </Suspense>
    </div>
  )
}

function HeaderBar({ onOpenSidebar, onOpenHelp }: { onOpenSidebar: () => void; onOpenHelp: () => void }) {
  const { t } = useTranslation()
  const { isActive } = useWorkspaceHeader()
  const { isActive: topologyIsActive } = useTopologyHeader()

  return (
    <header className="bg-white dark:bg-black border-b border-gray-200 dark:border-gray-800 z-20">
      {/* Main row */}
      <div className="h-14 flex items-center px-3 lg:px-6">
        {/* Left: logo */}
        <div className="flex items-center gap-2 lg:gap-4 shrink-0">
          <button
            onClick={onOpenSidebar}
            className="p-2 -ml-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
          <div className="hidden sm:block">
            <img src="/logo-vertical-75px.png" alt="Vigía" className="block dark:hidden h-10 w-auto" />
            <img src="/logo-vertical-200px-dark.png" alt="Vigía" className="hidden dark:block h-10 w-auto" />
          </div>
          <div className="sm:hidden">
            <img src="/logo-vertical-75px.png" alt="Vigía" className="block dark:hidden h-8 w-auto" />
            <img src="/logo-vertical-200px-dark.png" alt="Vigía" className="hidden dark:block h-8 w-auto" />
          </div>
        </div>

        {/* Center: workspace/topology picker or command palette hint — desktop only */}
        <div className="flex-1 hidden lg:flex justify-center px-4">
          {isActive && <WorkspacePicker />}
          {topologyIsActive && <TopologyLayoutPicker />}
          {!isActive && !topologyIsActive && (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
              className="flex items-center gap-2 px-4 py-1.5 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors max-w-md w-full"
            >
              <Search size={16} className="shrink-0" />
              <span className="flex-1 text-left truncate">{t('commandPalette.placeholder')}</span>
              <kbd className="shrink-0 text-[11px] font-mono px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-400 dark:text-gray-500">
                {navigator.platform?.includes('Mac') ? '\u2318K' : 'Ctrl+K'}
              </kbd>
            </button>
          )}
        </div>

        {/* Right: controls */}
        <div className="flex items-center gap-2 lg:gap-4 shrink-0 ml-auto">
          <NotificationBell />
          <button
            onClick={onOpenHelp}
            data-tour="header-help"
            className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
            aria-label={t('help.title', 'Help') + ' (Shift+?)'}
          title={`${t('help.title', 'Help')} (Shift+?)`}
          >
            <HelpCircle size={20} aria-hidden="true" />
          </button>
          <div className="flex items-center gap-1 lg:gap-2 bg-gray-50 dark:bg-gray-900/50 p-1 rounded-xl border border-gray-100 dark:border-gray-800">
            <LanguageSelector />
            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
            <ThemeToggle />
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('pcl-open-ai-chat'))}
            className="relative p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1"
            aria-label="Open Vigía"
            title="Vigía"
          >
            <VigiaIcon size={35} />
          </button>
        </div>
      </div>

      {/* Second row: workspace/topology picker — responsive only */}
      {isActive && (
        <div className="flex lg:hidden justify-center px-3 pb-2">
          <WorkspacePicker />
        </div>
      )}
      {topologyIsActive && (
        <div className="flex lg:hidden justify-center px-3 pb-2">
          <TopologyLayoutPicker />
        </div>
      )}
    </header>
  )
}

function ShimmerLogo() {
  const shimmerRef = useRef<HTMLDivElement>(null)
  const animatingRef = useRef(false)

  const triggerShimmer = useCallback(() => {
    if (animatingRef.current || !shimmerRef.current) return
    animatingRef.current = true
    const el = shimmerRef.current
    el.style.animation = 'none'
    void el.offsetWidth
    el.style.animation = 'shimmer-sweep 0.8s ease-in-out'
    const onEnd = () => {
      animatingRef.current = false
      el.removeEventListener('animationend', onEnd)
    }
    el.addEventListener('animationend', onEnd)
  }, [])

  // Auto-shimmer: once after 2s, then every 60s
  useEffect(() => {
    const initial = setTimeout(triggerShimmer, 2000)
    const interval = setInterval(triggerShimmer, 60000)
    return () => { clearTimeout(initial); clearInterval(interval) }
  }, [triggerShimmer])

  return (
    <a
      href="https://proconsi.com"
      target="_blank"
      rel="noopener noreferrer"
      className="relative overflow-hidden inline-block cursor-pointer rounded max-w-[70%]"
      onMouseEnter={triggerShimmer}
      title="Proconsi"
    >
      <img
        src="/proconsi-logo-solid.webp"
        alt="Proconsi"
        className="max-h-[60px] w-auto h-auto opacity-50 dark:invert dark:opacity-30 transition-opacity duration-300 hover:opacity-70 dark:hover:opacity-50"
      />
      <div
        ref={shimmerRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%)',
          backgroundSize: '200% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '-200% center',
          WebkitMaskImage: 'url(/proconsi-logo-solid.webp)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskImage: 'url(/proconsi-logo-solid.webp)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
        }}
      />
    </a>
  )
}

interface NavItemProps {
  to: string
  icon: ReactNode
  label: string
  badge?: number
  collapsed?: boolean
  onClick?: () => void
  dataTour?: string
}

function NavItem({ to, icon, label, badge, collapsed, onClick, dataTour }: NavItemProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const isCurrentRoute = location.pathname === to

  const handleClick = (e: React.MouseEvent) => {
    onClick?.()
    // If already on this route, navigate with reset param to clear filters without full reload
    if (isCurrentRoute) {
      e.preventDefault()
      navigate(`${to}?reset=${Date.now()}`, { replace: true })
    }
  }

  return (
    <li data-tour={dataTour}>
      <NavLink
        to={to}
        onClick={handleClick}
        onMouseEnter={() => preloadRoute(to)}
        onFocus={() => preloadRoute(to)}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          `relative flex items-center gap-3 px-3 py-2 lg:py-1.5 rounded-lg transition-all duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 focus-visible:outline-none ${
            collapsed ? 'lg:!gap-0 lg:!px-0 lg:justify-center' : ''
          } ${isActive
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20 font-medium'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-white'
          }`
        }
      >
        <span aria-hidden="true" className="shrink-0">{icon}</span>
        {!collapsed && <span className="flex-1">{label}</span>}
        {badge !== undefined && badge > 0 && (
          collapsed ? (
            <span className="hidden lg:block absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full motion-safe:animate-pulse shadow-lg shadow-red-500/30" />
          ) : (
            <span className="min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full px-1.5 motion-safe:animate-pulse shadow-lg shadow-red-500/30" aria-label={`${badge} ${badge === 1 ? 'notification' : 'notifications'}`}>
              {badge > 99 ? '99+' : badge}
            </span>
          )
        )}
      </NavLink>
    </li>
  )
}
